import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

// A raw, slash-escaped CSS variable for `background/secondary`, which is what the token actually
// compiles to: inside a `{colors.…}` reference Chakra reads the first `/` as its color-mix opacity
// modifier, so the token cannot be written that way within a compound value like a gradient.
const SURFACE_COLOR = 'var(--colors-background\\/secondary)';

const codeSnippetSlotRecipe = defineSlotRecipe({
  className: 'code-snippet',
  slots: [
    'root',
    'content',
    'copyButton',
    'copyButtonWrapper',
    'showMoreContainer',
    'showMoreGradient',
    'showMoreButton',
  ],
  base: {
    root: {
      background: 'background/secondary',
    },
    content: {
      flex: '1',
      minWidth: 0,
    },
    copyButton: {
      background: 'background/secondary',
      color: 'icon/secondary',
      cursor: 'pointer',
      _hover: {
        background: 'background/hover',
        color: 'icon/primary',
        _active: {
          background: 'background/active',
          color: 'icon/primary',
        },
      },
      _active: {
        background: 'background/active',
        color: 'icon/primary',
      },
      _focusVisible: {
        outlineOffset: '-3px',
      },
    },
  },
  variants: {
    variant: {
      inline: {
        root: {
          alignItems: 'center',
          borderRadius: '2',
          color: 'text/body',
          display: 'inline-flex',
          paddingBlock: '2',
          paddingInline: '6',
          whiteSpace: 'nowrap',
        },
      },
      single: {
        root: {
          alignItems: 'center',
          borderRadius: '4',
          display: 'flex',
          height: '40',
          isolation: 'isolate',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        },
        content: {
          alignItems: 'center',
          display: 'flex',
          height: '40',
          overflow: 'hidden',
          paddingInlineStart: '16',
          position: 'relative',
          _after: {
            background: `linear-gradient(to left, ${SURFACE_COLOR}, transparent)`,
            content: '""',
            height: '40',
            pointerEvents: 'none',
            position: 'absolute',
            right: 0,
            top: 0,
            width: '8',
          },
        },
        copyButton: {
          flexShrink: '0',
          padding: '12',
        },
      },
      multi: {
        root: {
          borderRadius: '4',
          display: 'flex',
          flexDirection: 'column',
          padding: '16',
          position: 'relative',
        },
        content: {
          color: 'text/body',
          overflowY: 'auto',
          transition: 'max-height 0.3s ease',
          whiteSpace: 'pre-wrap',
        },
        copyButton: {
          borderRadius: '4',
          overflow: 'hidden',
          padding: '8',
        },
        copyButtonWrapper: {
          background: 'background/secondary',
          padding: '4',
          position: 'absolute',
          right: 0,
          top: 0,
        },
        showMoreContainer: {
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'flex-end',
        },
        showMoreGradient: {
          background: `linear-gradient(to top, ${SURFACE_COLOR} 33%, transparent)`,
          borderBottomRadius: '4',
          bottom: 0,
          height: '40',
          left: 0,
          pointerEvents: 'none',
          position: 'absolute',
          right: 0,
        },
        showMoreButton: {
          alignItems: 'center',
          background: 'background/secondary',
          borderRadius: '4',
          color: 'text/secondary',
          cursor: 'pointer',
          display: 'flex',
          fontFamily: 'body',
          gap: '4',
          justifyContent: 'center',
          minWidth: '64',
          paddingBlock: '8',
          paddingInline: '12',
          position: 'relative',
          textStyle: 'comp/button/md',
          _hover: {
            background: 'background/hover',
            color: 'text/primary',
            _active: {
              background: 'background/active',
              color: 'text/primary',
              overflow: 'hidden',
            },
          },
          _active: {
            background: 'background/active',
            color: 'text/primary',
            overflow: 'hidden',
          },
          _focusVisible: {
            outlineOffset: '-3px',
          },
        },
      },
    },
    size: {
      md: {
        root: { textStyle: 'code/md' },
        content: { textStyle: 'code/md' },
      },
      lg: {
        root: { textStyle: 'code/lg' },
        content: { textStyle: 'code/lg' },
      },
    },
    interactive: {
      true: {},
      false: {},
    },
    hasShowMore: {
      true: {},
      false: {},
    },
    wrapLines: {
      true: {},
      false: {},
    },
    isExpanded: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    // Indented code (YAML, shell) loses its structure when a long line wraps: the continuation starts
    // at the left edge, so a nested key reads like a top-level one. `wrapLines={false}` keeps lines whole
    // behind a horizontal scrollbar instead. Only `multi` wraps in the first place.
    {
      variant: 'multi',
      wrapLines: false,
      css: {
        content: {
          overflowX: 'auto',
          whiteSpace: 'pre',
        },
      },
    },
    {
      variant: 'inline',
      interactive: true,
      css: {
        root: {
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover:not(:active)': {
            background: 'background/hover',
          },
          '&:active': {
            background: 'background/active',
          },
        },
      },
    },
    {
      variant: 'multi',
      hasShowMore: true,
      css: {
        root: { paddingBlockEnd: 0, paddingInlineEnd: '8' },
      },
    },
    {
      variant: 'multi',
      hasShowMore: true,
      isExpanded: false,
      css: {
        showMoreContainer: { bottom: 0, left: 0, position: 'absolute', right: 0 },
      },
    },
    {
      variant: 'multi',
      hasShowMore: true,
      isExpanded: true,
      css: {
        root: { padding: 0, paddingInlineEnd: 0 },
        content: { overflow: 'auto', padding: '16', paddingBlockEnd: 0 },
        showMoreContainer: { position: 'relative' },
      },
    },
  ],
  defaultVariants: {
    interactive: false,
    size: 'lg',
    variant: 'single',
    wrapLines: true,
  },
});

export default codeSnippetSlotRecipe;
